import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { posthog, initPosthog } from './posthog';
import { Sentry } from './sentry';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    tenantId: string;
    /** On the wire since forever (POST /auth/login returns it); the type was
     *  just narrower than the payload, so the profile avatar could not be
     *  rendered without a cast. Verified against the running API 2026-09-18. */
    avatarUrl?: string | null;
  } | null;
  tenant: {
    id: string;
    name: string;
    slug: string;
    tier: string;
    settings: Record<string, unknown>;
  } | null;

  setAuth: (data: { user: any; tokens: { accessToken: string; refreshToken: string }; tenant?: any }) => void;
  /**
   * Single-flight. Concurrent callers share one in-flight POST /auth/refresh;
   * see `refreshInFlight` below for why that is a correctness requirement and
   * not an optimisation.
   */
  refresh: () => Promise<boolean>;
  /**
   * Self-heal a store that holds tokens but no user/tenant (2026-09-19, prod:
   * the sidebar showed "Workspace · Member · ? · Account"). Nothing in this
   * file writes that shape, but a token injected into localStorage by a tool,
   * or a persisted state from an older build, produces it — and nothing ever
   * re-fetched the identity afterwards. Fetches /auth/me and /auth/tenant once.
   */
  hydrate: () => Promise<void>;
  logout: () => void;
  updateTenantSettings: (patch: Record<string, unknown>) => void;
  /** Merge fields into the signed-in user after a successful PATCH /auth/me. */
  updateUser: (patch: Partial<NonNullable<AuthState['user']>>) => void;
}

/**
 * The one in-flight refresh, shared by every caller (2026-10-21).
 *
 * The server's POST /auth/refresh is single-use with rotation: auth/service.ts
 * `refreshAuthToken` looks the token row up and DELETES it before issuing the
 * next pair. So the stored refresh token can be spent exactly once, and whoever
 * presents it second gets a 401.
 *
 * Without this guard that was a guaranteed logout at `JWT_EXPIRY` (15m by
 * default) on any screen with more than one request in flight: the access
 * token expires, every pending fetch 401s in the same tick, each one calls
 * refresh() with the SAME stored token, one wins and the losers get false ->
 * client.ts logs out and redirects to /login. Reproduced in a real browser on
 * 2026-10-21 — 12 concurrent 401s on /home produced 12 concurrent refresh
 * calls, a mix of 200s and 401s, and a bounce to /login.
 *
 * Module-scoped on purpose: it must be shared across every caller of the
 * single store instance, and it must NOT be part of persisted state.
 */
let refreshInFlight: Promise<boolean> | null = null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      tenant: null,

      setAuth: ({ user, tokens, tenant }) => {
        set({
          user,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          tenant: tenant || get().tenant,
        });
        // Analytics: dashboard-only. setAuth is reached exclusively after a
        // successful login/signup — never from a public route — so this is
        // one of the two call sites allowed to turn PostHog on (see
        // lib/posthog.ts). Must init before identify() or identify is a no-op.
        initPosthog();
        posthog.identify(user.id, {
          email: user.email,
          name: user.name,
          tenantId: tenant?.id,
          tier: tenant?.tier,
        });
        if (tenant?.id) posthog.group('tenant', tenant.id, { name: tenant.name, tier: tenant.tier });
        Sentry.setUser({ id: user.id, email: user.email });
        Sentry.setTag('tenant_id', tenant?.id || 'unknown');
      },

      refresh: () => {
        // A 401 that lands while a refresh is already running joins it instead
        // of spending a second (now-dead) refresh token. The awaiting caller
        // then re-reads accessToken from the store and retries — client.ts
        // already does exactly that.
        if (refreshInFlight) return refreshInFlight;

        refreshInFlight = (async () => {
          const { refreshToken } = get();
          if (!refreshToken) return false;
          try {
            const res = await fetch('/api/auth/refresh', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refreshToken }),
            });
            if (!res.ok) return false;
            const data = await res.json();
            const nextAccessToken = data?.tokens?.accessToken ?? data?.accessToken;
            const nextRefreshToken = data?.tokens?.refreshToken ?? data?.refreshToken;
            if (!nextAccessToken || !nextRefreshToken) {
              return false;
            }
            set({ accessToken: nextAccessToken, refreshToken: nextRefreshToken });
            return true;
          } catch {
            return false;
          }
        })();

        // Released once this flight settles, so the NEXT expiry (15 minutes
        // later) opens a fresh one. A 401 that arrives after the release does
        // its own refresh — correctly, with the token this flight rotated in.
        return refreshInFlight.finally(() => { refreshInFlight = null; });
      },

      hydrate: async () => {
        const { accessToken, user, tenant } = get();
        if (!accessToken || (user && tenant)) return;
        try {
          const headers = { Authorization: `Bearer ${accessToken}` };
          const [meRes, tenantRes] = await Promise.all([
            user ? null : fetch('/api/auth/me', { headers }),
            tenant ? null : fetch('/api/auth/tenant', { headers }),
          ]);
          const patch: Partial<Pick<AuthState, 'user' | 'tenant'>> = {};
          if (meRes?.ok) { const d = await meRes.json(); if (d?.user) patch.user = d.user; }
          if (tenantRes?.ok) { const d = await tenantRes.json(); if (d?.tenant) patch.tenant = d.tenant; }
          if (Object.keys(patch).length) set(patch);
        } catch {
          // Leave the fallbacks in place; a 401 here is handled by request()'s refresh path.
        }
      },

      // Optimistic local merge into tenant.settings — used so a settings PATCH
      // (e.g. checklistDismissed) is reflected immediately in every mounted
      // component reading the store, without waiting on a re-fetch. Persisted
      // to localStorage by the zustand `persist` middleware like any other set().
      updateUser: (patch) =>
        set((state) => (state.user ? { user: { ...state.user, ...patch } } : {})),

      updateTenantSettings: (patch) => {
        const tenant = get().tenant;
        if (!tenant) return;
        set({ tenant: { ...tenant, settings: { ...tenant.settings, ...patch } } });
      },

      logout: () => {
        const { accessToken, refreshToken } = get();
        if (accessToken && refreshToken) {
          fetch('/api/auth/logout', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ refreshToken }),
          }).catch(() => {});
        }
        set({ accessToken: null, refreshToken: null, user: null, tenant: null });
        posthog.reset();
        Sentry.setUser(null);
        Sentry.setTag('tenant_id', undefined);
      },
    }),
    { name: 'foxet-auth' },
  ),
);
